"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MERCHANT_STEPS = exports.TRANSFER_PIPELINE = exports.completionBlockers = exports.transferProgress = exports.serializeStages = exports.stagesForMode = exports.BUSINESS_TRANSFER_STAGES_V1 = exports.STORE_TRANSFER_STAGES_V1 = exports.TRANSFER_OWNER_LABEL = exports.TRANSFER_LANES = exports.TRANSFER_OWNERS = void 0;
exports.resolveTransferJourney = resolveTransferJourney;
exports.waitingOn = waitingOn;
exports.deriveTransferStatus = deriveTransferStatus;
exports.merchantSteps = merchantSteps;
const orderJourney_1 = require("./orderJourney");
const businessTransfer_1 = require("./businessTransfer");
/**
 * THE TRANSFER, AS STAGES — the same model as the order journey
 * (`orderJourney.ts`): a main spine, a fork into lanes that run at once, then
 * the spine again. The stage list is DATA, so adding or removing a step is a
 * list change. Evidence is a function ON the definition (not a switch), which
 * is what lets a stage be attached or detached without touching the resolver.
 */
exports.TRANSFER_OWNERS = ['seller', 'buyer', 'rubytill', 'iso', 'system'];
exports.TRANSFER_LANES = ['main', 'card', 'data', 'devices'];
exports.TRANSFER_OWNER_LABEL = {
    seller: 'Seller', buyer: 'Buyer', rubytill: 'Us', iso: 'Payments partner', system: 'Automatic',
};
const activeCompletion = (list, stageId) => [...list].filter((c) => c.stageId === stageId).sort((a, b) => b.at - a.at)[0];
const takeoverCount = (t) => {
    const units = t.dryRun?.leasedUnits ?? [];
    return units.filter((u) => (t.options.leaseChoices.find((c) => c.unitId === u.unitId)?.choice ?? t.options.leaseDefault) === 'takeover').length;
};
/* ─────────────── shared stage definitions ─────────────── */
const requested = {
    id: 'requested', label: 'Request sent', owner: 'seller', completion: 'evidence',
    evidence: ({ transfer: t }) => ({ done: true, at: t.createdAt, detail: `By ${t.requestedBy.name || t.requestedBy.email}` }),
};
const buyerAccepted = {
    id: 'buyer_accepted', label: 'Buyer accepted', owner: 'buyer', completion: 'evidence',
    hint: 'The buyer opens the invite link and accepts.',
    evidence: ({ transfer: t }) => ({ done: !!t.buyer.acceptedAt, at: t.buyer.acceptedAt, detail: t.buyer.acceptedAt ? `${t.buyer.name}` : `Invite sent to ${t.buyer.email}` }),
};
const agreementSigned = {
    id: 'agreement_signed', label: 'Transfer agreement signed', owner: 'buyer', completion: 'evidence',
    evidence: ({ transfer: t }) => ({ done: !!t.buyer.signature, at: t.buyer.signature?.signedAt, detail: t.buyer.signature ? `Signed by ${t.buyer.signature.typedName || t.buyer.name}` : 'Not signed yet' }),
};
const buyerCard = {
    id: 'buyer_card', label: 'Buyer card on file', owner: 'buyer', completion: 'evidence',
    evidence: ({ transfer: t }) => {
        const pm = t.buyer.paymentMethod;
        return { done: !!pm, at: pm?.savedAt, detail: pm ? `${pm.brand || 'Card'} ending ${pm.last4 || '····'}` : 'No card yet' };
    },
};
const complianceDocs = {
    id: 'compliance_docs', label: 'Compliance documents', lane: 'card', owner: 'buyer', completion: 'manual',
    hint: "The buyer's business documents for the new merchant account.",
};
const processorSelected = {
    id: 'processor_selected', label: 'Processor selected', lane: 'card', owner: 'rubytill', completion: 'manual',
};
const isoProcessing = {
    id: 'iso_processing', label: 'ISO processing · VAR sheet', lane: 'card', owner: 'iso', completion: 'manual',
    hint: 'The payments partner boards the buyer and issues the VAR sheet.',
};
const varSheetReceived = {
    id: 'var_sheet_received', label: 'VAR sheet received', lane: 'card', owner: 'rubytill', completion: 'evidence',
    evidence: ({ transfer: t }) => ({ done: !!t.varSheet, at: t.varSheet?.uploadedAt, detail: t.varSheet ? `MID ${t.varSheet.mid}` : 'Upload the VAR sheet' }),
};
const scheduled = {
    id: 'scheduled', label: 'Cut-over scheduled', owner: 'rubytill', completion: 'evidence', needsAllLanes: true,
    evidence: ({ transfer: t }) => ({ done: !!t.runAt || !!t.run, at: t.runAt, detail: t.runAt ? `Runs ${new Date(t.runAt).toISOString()}` : 'After close, once all lanes are done' }),
};
const merchantLink = {
    id: 'merchant_link', label: 'Link new merchant account', owner: 'rubytill', completion: 'evidence', blocksCompletion: true,
    hint: "Manual: bind the buyer's VAR sheet to each card device. Overnight or any time.",
    skipWhen: ({ transfer: t }) => (t.run?.status === 'done' && t.links.length === 0 ? 'No card devices' : false),
    evidence: ({ transfer: t }) => {
        const saved = t.links.filter((l) => l.credentialsSavedAt && l.parametersDownloadedAt).length;
        const all = t.links.length > 0 && saved === t.links.length;
        const at = all ? Math.max(...t.links.map((l) => l.parametersDownloadedAt || 0)) : undefined;
        return { done: all, at, detail: t.run?.status === 'done' ? `${saved} of ${t.links.length} linked` : 'After the transfer runs' };
    },
};
const testSales = {
    id: 'test_sales', label: 'Test card sale per device', owner: 'rubytill', completion: 'evidence', blocksCompletion: true,
    skipWhen: ({ transfer: t }) => (t.run?.status === 'done' && t.links.length === 0 ? 'No card devices' : false),
    evidence: ({ transfer: t }) => {
        const done = t.links.filter(businessTransfer_1.isLinkComplete).length;
        const all = t.links.length > 0 && done === t.links.length;
        return { done: all, at: all ? Math.max(...t.links.map((l) => l.testSaleAt || 0)) : undefined, detail: `${done} of ${t.links.length} tested` };
    },
};
const complete = {
    id: 'complete', label: 'Transfer complete', owner: 'system', completion: 'evidence',
    evidence: ({ transfer: t }) => ({ done: !!t.completedAt, at: t.completedAt, detail: t.completedAt ? 'Emails sent · history written' : 'When every step above is done' }),
};
/* ─────────────── store mode ─────────────── */
exports.STORE_TRANSFER_STAGES_V1 = [
    requested, buyerAccepted, agreementSigned, buyerCard,
    complianceDocs, processorSelected, isoProcessing, varSheetReceived,
    {
        id: 'dry_run', label: 'Dry run', lane: 'data', owner: 'system', completion: 'evidence',
        evidence: ({ transfer: t }) => ({ done: !!t.dryRun, at: t.dryRun?.ranAt, detail: t.dryRun ? 'Nothing written' : 'Runs automatically after the request' }),
    },
    {
        id: 'preflight', label: 'Pre-flight checks', lane: 'data', owner: 'seller', completion: 'evidence',
        evidence: ({ transfer: t }) => {
            if (!t.dryRun)
                return { done: false, detail: 'Waiting for the dry run' };
            const b = t.dryRun.blockers;
            return b.length ? { done: false, blocked: true, detail: b[0].message + (b.length > 1 ? ` (+${b.length - 1} more)` : '') } : { done: true, at: t.dryRun.ranAt, detail: 'All clear' };
        },
    },
    { id: 'dry_run_approved', label: 'Dry run approved', lane: 'data', owner: 'rubytill', completion: 'manual' },
    {
        id: 'lease_choices', label: 'Lease choice per device', lane: 'devices', owner: 'rubytill', completion: 'evidence',
        evidence: ({ transfer: t }) => {
            if (!t.dryRun)
                return { done: false, detail: 'Waiting for the dry run' };
            const units = t.dryRun.leasedUnits;
            if (!units.length)
                return { done: true, at: t.dryRun.ranAt, detail: 'No leased devices' };
            const take = takeoverCount(t);
            return { done: true, at: t.dryRun.ranAt, detail: `${units.length - take} pay off · ${take} take over` };
        },
    },
    {
        id: 'buyer_lease_signed', label: 'Buyer lease signed', lane: 'devices', owner: 'buyer', completion: 'evidence',
        skipWhen: ({ transfer: t }) => (takeoverCount(t) === 0 ? 'Nothing taken over' : false),
        evidence: ({ transfer: t }) => ({ done: !!t.buyer.leaseSignature, at: t.buyer.leaseSignature?.signedAt, detail: t.buyer.leaseSignature ? 'Signed' : 'Sent to the buyer to sign' }),
    },
    { id: 'device_list_confirmed', label: 'Device list confirmed', lane: 'devices', owner: 'rubytill', completion: 'manual' },
    scheduled,
    {
        id: 'transfer_run', label: 'Transfer runs', owner: 'system', completion: 'evidence',
        hint: "Moves data and devices, and detaches the seller's merchant account from every card device.",
        evidence: ({ transfer: t }) => {
            const r = t.run;
            if (!r)
                return { done: false, detail: 'Not started' };
            if (r.status === 'failed')
                return { done: false, blocked: true, detail: r.steps.find((s) => s.status === 'failed')?.error || 'A step failed' };
            if (r.status === 'running')
                return { done: false, detail: `Running · ${r.steps.filter((s) => s.status === 'done').length} of ${r.steps.length}` };
            return { done: true, at: r.finishedAt, detail: 'Card payments pending until linked' };
        },
    },
    merchantLink, testSales,
    {
        id: 'tills_repaired', label: 'Tills re-paired', owner: 'buyer', completion: 'evidence',
        skipWhen: ({ transfer: t }) => (t.run?.status === 'done' && t.terminals.length === 0 ? 'No tills' : false),
        evidence: ({ transfer: t }) => {
            const n = t.terminals.filter((x) => x.activatedAt).length;
            const all = t.terminals.length > 0 && n === t.terminals.length;
            return { done: all, at: all ? Math.max(...t.terminals.map((x) => x.activatedAt || 0)) : undefined, detail: `${n} of ${t.terminals.length} re-paired` };
        },
    },
    complete,
];
/* ─────────────── business mode ─────────────── */
exports.BUSINESS_TRANSFER_STAGES_V1 = [
    requested, buyerAccepted, agreementSigned, buyerCard,
    complianceDocs, processorSelected, isoProcessing, varSheetReceived,
    {
        id: 'dry_run', label: 'Owner check', lane: 'data', owner: 'system', completion: 'evidence',
        evidence: ({ transfer: t }) => ({ done: !!t.dryRun, at: t.dryRun?.ranAt, detail: t.dryRun ? 'Checked' : 'Runs automatically after the request' }),
    },
    { id: 'owner_review', label: 'Owner change reviewed', lane: 'data', owner: 'rubytill', completion: 'manual' },
    scheduled,
    {
        id: 'owner_changed', label: 'Owner changed', owner: 'system', completion: 'evidence',
        evidence: ({ transfer: t }) => {
            const r = t.run;
            if (!r)
                return { done: false, detail: 'Not started' };
            if (r.status === 'failed')
                return { done: false, blocked: true, detail: r.steps.find((s) => s.status === 'failed')?.error || 'A step failed' };
            return { done: r.status === 'done', at: r.finishedAt, detail: r.status === 'done' ? 'New owner in place · card payments pending' : 'Running' };
        },
    },
    merchantLink, testSales, complete,
];
const stagesForMode = (mode) => mode === 'store' ? exports.STORE_TRANSFER_STAGES_V1 : exports.BUSINESS_TRANSFER_STAGES_V1;
exports.stagesForMode = stagesForMode;
/* ─────────────── resolver ─────────────── */
function resolveTransferJourney(stages, ctx) {
    const t = ctx.transfer;
    const now = ctx.now ?? Date.now();
    const rows = stages.map((def) => {
        const skip = def.skipWhen?.(ctx);
        if (skip)
            return { def, state: 'skipped', detail: skip };
        if (def.completion === 'manual') {
            const c = activeCompletion(t.completions, def.id);
            if (c && !c.undoneAt)
                return { def, state: 'done', at: c.at, detail: `Marked done by ${c.by.name}.`, assertedBy: c.by };
            return { def, state: 'todo', detail: def.hint || 'Waiting on a person.' };
        }
        const ev = def.evidence ? def.evidence(ctx) : { done: false, detail: '' };
        return { def, state: ev.done ? 'done' : ev.blocked ? 'blocked' : 'todo', at: ev.done ? ev.at : undefined, detail: ev.detail };
    });
    const lanesDone = rows.filter((r) => r.def.lane).every((r) => r.state === 'done' || r.state === 'skipped');
    const laneOf = (r) => r.def.lane ?? 'main';
    const started = new Map();
    let handover = t.createdAt;
    for (const r of rows) {
        if (r.state === 'done' && r.at) {
            started.set(laneOf(r), r.at);
            if (laneOf(r) === 'main')
                handover = r.at;
        }
    }
    const seen = new Set();
    for (const r of rows) {
        if (r.state !== 'todo' && r.state !== 'blocked')
            continue;
        const lane = laneOf(r);
        if (seen.has(lane))
            continue;
        seen.add(lane);
        if (r.def.needsAllLanes && !lanesDone) {
            r.detail = 'Waiting for card processing, data and devices to finish.';
            continue;
        }
        if (r.state === 'todo')
            r.state = 'active';
        r.activeSince = started.get(lane) ?? handover;
        r.age = (0, orderJourney_1.stageAge)(r.activeSince, now);
    }
    return rows;
}
const serializeStages = (rows) => rows.map((r) => ({
    id: r.def.id, label: r.def.label, lane: r.def.lane ?? 'main', owner: r.def.owner,
    hint: r.def.hint, completion: r.def.completion,
    blocksCompletion: !!r.def.blocksCompletion, needsAllLanes: !!r.def.needsAllLanes,
    state: r.state, detail: r.detail, at: r.at, assertedBy: r.assertedBy,
    activeSince: r.activeSince, age: r.age,
}));
exports.serializeStages = serializeStages;
const transferProgress = (rows) => ({
    done: rows.filter((r) => r.state === 'done').length,
    total: rows.filter((r) => r.state !== 'skipped').length,
});
exports.transferProgress = transferProgress;
const completionBlockers = (rows) => rows.filter((r) => r.def.id !== 'complete' && r.state !== 'done' && r.state !== 'skipped');
exports.completionBlockers = completionBlockers;
/** Whose move it is: the first blocked row, else the first active main row, else the first active lane row. */
function waitingOn(rows) {
    const blocked = rows.find((r) => r.state === 'blocked');
    if (blocked)
        return blocked.def.owner;
    const main = rows.find((r) => r.state === 'active' && !r.def.lane);
    if (main)
        return main.def.owner;
    return rows.find((r) => r.state === 'active')?.def.owner;
}
const stageDone = (rows, id) => {
    const r = rows.find((x) => x.def.id === id);
    return !r || r.state === 'done' || r.state === 'skipped';
};
function deriveTransferStatus(t, rows) {
    if (t.cancelledAt)
        return 'cancelled';
    if (t.completedAt)
        return 'completed';
    if (t.run?.status === 'failed')
        return 'failed';
    if (t.run?.status === 'running')
        return 'running';
    if (t.run?.status === 'done')
        return 'awaiting_merchant_link';
    if (t.runAt)
        return 'scheduled';
    if (!['buyer_accepted', 'agreement_signed', 'buyer_card'].every((id) => stageDone(rows, id)))
        return 'buyer_onboarding';
    if (rows.filter((r) => r.def.lane === 'card').some((r) => r.state !== 'done' && r.state !== 'skipped'))
        return 'card_processing';
    if (rows.filter((r) => r.def.lane).some((r) => r.state !== 'done' && r.state !== 'skipped'))
        return 'card_processing';
    return 'ready_to_run';
}
exports.TRANSFER_PIPELINE = [
    { status: 'buyer_onboarding', label: 'Buyer onboarding' },
    { status: 'card_processing', label: 'Card processing' },
    { status: 'ready_to_run', label: 'Ready to run' },
    { status: 'scheduled', label: 'Scheduled' },
    { status: 'awaiting_merchant_link', label: 'Awaiting merchant link' },
    { status: 'completed', label: 'Completed' },
];
/** The merchant sees 8 plain steps; each groups one or more stages. */
exports.MERCHANT_STEPS = [
    { id: 'sent', label: 'Request sent', stageIds: ['requested'], modes: ['store', 'business'] },
    { id: 'accepted', label: 'Buyer accepted and signed', stageIds: ['buyer_accepted', 'agreement_signed', 'buyer_card'], modes: ['store', 'business'] },
    { id: 'review', label: 'RubyTill reviewed the transfer', stageIds: ['dry_run', 'preflight', 'dry_run_approved', 'owner_review', 'lease_choices', 'buyer_lease_signed', 'device_list_confirmed'], modes: ['store', 'business'] },
    { id: 'merchant_setup', label: "Buyer's merchant account set up", stageIds: ['compliance_docs', 'processor_selected', 'iso_processing', 'var_sheet_received'], modes: ['store', 'business'] },
    { id: 'scheduled', label: 'Transfer night booked', stageIds: ['scheduled'], modes: ['store', 'business'] },
    { id: 'night', label: 'Transfer night', stageIds: ['transfer_run', 'owner_changed'], modes: ['store', 'business'] },
    { id: 'linked', label: 'New merchant account linked', stageIds: ['merchant_link', 'test_sales', 'tills_repaired'], modes: ['store', 'business'] },
    { id: 'done', label: 'Transferred', stageIds: ['complete'], modes: ['store', 'business'] },
];
function merchantSteps(rows, mode) {
    const steps = exports.MERCHANT_STEPS.filter((s) => s.modes.includes(mode)).map((s) => {
        const own = rows.filter((r) => s.stageIds.includes(r.def.id));
        const done = own.length > 0 && own.every((r) => r.state === 'done' || r.state === 'skipped');
        const at = done ? Math.max(0, ...own.map((r) => r.at || 0)) || undefined : undefined;
        const live = own.find((r) => r.state === 'blocked' || r.state === 'active');
        return { id: s.id, label: s.label, state: (done ? 'done' : 'todo'), at, detail: live?.detail || own.find((r) => r.state !== 'done')?.detail || '' };
    });
    const first = steps.findIndex((s) => s.state !== 'done');
    if (first >= 0)
        steps[first] = { ...steps[first], state: 'current' };
    return steps;
}
//# sourceMappingURL=transferJourney.js.map